import { useHandler } from "@redotech/react-util/hook";
import { REDO_INTENT_ACTIVE_CART_ATTRIBUTE_KEY } from "@redotech/redo-storefront-common/cart-attributes";
import { memo, useEffect } from "react";
import { StorefrontClient } from "../../client/storefront";

/**
 * Stamps the cart with `redo_intent_active=true` exactly once per page load
 * when the intent system is mounted. The mount gate in
 * `redo-storefront/index.tsx` already verifies that intent is enabled and a
 * hazard endpoint is configured for this team, so this component just needs
 * to ensure the attribute is present on the cart — no probability fetch
 * required.
 *
 * The order webhook reads this attribute to bill orders that were touched by
 * intent (when the team has `billIntentActiveOrders` enabled) and then scrubs
 * it before the merchant ever sees the order.
 */
export const IntentActiveCartAttribute = memo(
  function IntentActiveCartAttribute({
    storefrontClient,
  }: {
    storefrontClient: StorefrontClient;
  }) {
    const stampIntentActive = useHandler(async () => {
      try {
        const cart = await storefrontClient.cartGet();
        if (
          cart.attributes?.[REDO_INTENT_ACTIVE_CART_ATTRIBUTE_KEY] === "true"
        ) {
          return;
        }
        await storefrontClient.updateCartAttribute({
          attributes: [
            { key: REDO_INTENT_ACTIVE_CART_ATTRIBUTE_KEY, value: "true" },
          ],
        });
      } catch (err) {
        console.error("Failed to write redo_intent_active cart attribute", err);
      }
    });

    useEffect(() => {
      void stampIntentActive();
    }, [stampIntentActive]);

    return null;
  },
);
